import React from "react";
import { isSameDay, isBefore, isWithinInterval, startOfDay } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faComments,
  faLocationDot,
} from "@fortawesome/pro-regular-svg-icons";
import formatDate from "../../../helpers/formatDate";
import CalendarLinks, { WidgetCalendar } from "./CalendarLinks";

export type Appointment = {
  id: string;
  name: string;
  start: string;
  end?: string;
  all_day?: boolean;
  path: string;
  meeting?: {
    join_url?: string;
  };
  location?: string;
  canceled?: boolean;
  // The appointment's calendars, delivered only when the widget has
  // "Link zum Kalender" switched on (tixxt/core#469, #743)
  calendars?: WidgetCalendar[];
};

function displayTime(appointment: Appointment) {
  const now = new Date();
  const startTime = new Date(appointment.start);
  const allDay = appointment.all_day;
  if (appointment.end) {
    const endTime = new Date(appointment.end);

    const nowInRange = isWithinInterval(startOfDay(now), {
      start: startOfDay(startTime),
      end: startOfDay(endTime),
    });

    if (nowInRange) {
      // today
      if (isSameDay(startTime, endTime)) {
        if (!allDay) {
          // Case-3
          return (
            <>
              {`${formatDate(startTime, "p")} - ${formatDate(endTime, "p")}, `}
              <strong>{I18n.t("js.widgets.appointment.today")}</strong>
            </>
          );
        }
      } else {
        if (isSameDay(now, startTime)) {
          if (allDay) {
            // Case-7 Case-12
            return (
              <>
                <strong>{I18n.t("js.widgets.appointment.today")}</strong>
                {` -`}
                <br />
                {`${formatDate(endTime, "EEEEEE")}., ${formatDate(endTime, "P")}`}
              </>
            );
          } else {
            // Case-10 Case-16
            return (
              <>
                {`${formatDate(startTime, "p")}, `}
                <strong>{I18n.t("js.widgets.appointment.today")}</strong>
                {` -`}
                <br />
                {`${formatDate(endTime, "EEEEEE")}., ${formatDate(endTime, "P")}, ${formatDate(endTime, "p")}`}
              </>
            );
          }
        } else {
          if (isSameDay(now, endTime)) {
            if (allDay) {
              // Case-8 Case-14
              return <strong>{I18n.t("js.widgets.appointment.today")}</strong>;
            } else {
              // Case-11 Case-18
              return (
                <>
                  {I18n.t("js.widget.appointment.until")}
                  {` ${formatDate(endTime, "p")}, `}
                  <strong>{I18n.t("js.widgets.appointment.today")}</strong>
                </>
              );
            }
          } else {
            if (allDay) {
              // Case-13
              return (
                <>
                  <strong>{I18n.t("js.widgets.appointment.today")}</strong>
                  {" -"}
                  <br />
                  {`${formatDate(endTime, "EEEEEE")}., ${formatDate(endTime, "P")}`}
                </>
              );
            } else {
              // Case-17
              return (
                <>
                  <strong>{I18n.t("js.widgets.appointment.today")}</strong>
                  {" -"}
                  <br />
                  {`${formatDate(endTime, "EEEEEE")}., ${formatDate(endTime, "P")}, ${formatDate(endTime, "p")}`}
                </>
              );
            }
          }
        }
      }
    } else {
      // future
      if (isSameDay(startTime, endTime)) {
        if (allDay) {
          // Case-4
          return `${formatDate(startTime, "EEEEEE")}., ${formatDate(startTime, "P")}`;
        } else {
          // Case-6
          return `${formatDate(startTime, "EEEEEE")}., ${formatDate(startTime, "P")}, ${formatDate(startTime, "p")} - ${formatDate(endTime, "p")}`;
        }
      } else {
        if (allDay) {
          // Case-9 Case-15
          return `${formatDate(startTime, "EEEEEE")}., ${formatDate(startTime, "P")} - ${formatDate(endTime, "EEEEEE")}., ${formatDate(endTime, "P")}`;
        } else {
          // Case-19
          return `${formatDate(startTime, "EEEEEE")}., ${formatDate(startTime, "P")}, ${formatDate(startTime, "p")} - ${formatDate(endTime, "EEEEEE")}., ${formatDate(endTime, "P")}, ${formatDate(endTime, "p")}`;
        }
      }
    }
  } else {
    if (isSameDay(now, startTime)) {
      if (allDay) {
        // Case-1
        return <strong>{I18n.t("js.widgets.appointment.today")}</strong>;
      } else {
        // Case-2
        return (
          <>
            {`${formatDate(startTime, "p")}, `}
            <strong>{I18n.t("js.widgets.appointment.today")}</strong>
          </>
        );
      }
    } else {
      if (allDay) {
        // Case-4
        return `${formatDate(startTime, "EEEEEE")}., ${formatDate(startTime, "P")}`;
      } else {
        // Case-5
        return `${formatDate(startTime, "EEEEEE")}., ${formatDate(startTime, "P")}, ${formatDate(startTime, "p")}`;
      }
    }
  }
}

type AppointmentRowProps = {
  appointment: Appointment;
  // Which parts of the row the appointments widget shows besides name and date
  // (tixxt/core#473), in the channel and in the info bars alike. Optional
  // because a payload rendered from before the selection existed carries no
  // such attribute, and that row shows everything, as it did.
  fields?: string[];
};

const AppointmentRow: React.FC<AppointmentRowProps> = ({
  appointment,
  fields,
}) => {
  const now = new Date();
  const startTime = new Date(appointment.start);
  const shows = (field: string) => !fields || fields.includes(field);

  return (
    <li className="appointment flex flex-col py-2 first:pt-0 last:pb-0">
      {/* The gap belongs on the icon, not on the line: `space-x-*` only
          separates element children, and the date and location of a row are
          plain text, so their icon would keep the gap in one row (the bold
          "today") and lose it in the next. */}
      <span>
        {shows("meeting") &&
          !appointment.canceled &&
          appointment.meeting?.join_url && (
            <FontAwesomeIcon icon={faComments} fixedWidth className="mr-1" />
          )}
        {/* semibold title over small secondary lines; running appointments
            keep their bold highlight */}
        <a
          className={`appointment-title font-semibold ${
            !isBefore(now, startTime) ? "font-bold" : ""
          } ${appointment.canceled ? "line-through" : ""}`}
          href={appointment.path}
        >
          {appointment.name}
        </a>
      </span>
      {appointment.canceled && (
        <span className="text-xs text-danger">
          {I18n.t("js.calendars.appointment.canceled")}
        </span>
      )}
      {appointment.calendars && appointment.calendars.length > 0 && (
        <CalendarLinks calendars={appointment.calendars} small />
      )}
      <span className={"appointment--timeDisplay text-xs"}>
        <FontAwesomeIcon icon={faCalendar} fixedWidth className="mr-1" />
        {displayTime(appointment)}
      </span>
      {shows("location") && appointment.location && (
        <span className={"appointment--location text-xs"}>
          <FontAwesomeIcon icon={faLocationDot} fixedWidth className="mr-1" />
          {appointment.location}
        </span>
      )}
    </li>
  );
};

export default AppointmentRow;
